import type { Residence } from '@repo/sdk/gen'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { type TKey, useTranslation } from '@/lib/i18n'
import { ResidencePhotos } from '@/routes/(private)/-components/residence-photos'
import {
	ContractFields,
	GeneralFields,
	GroupErrorDot,
	PecFields,
	type ResidenceFieldGroup,
	SeatsFields,
	ServicesFields
} from '../../-components/residence-form'
import { SitesFields } from '../../-components/sites-fields'
import { LocationSection } from './location-section'
import { MailboxTestSection } from './mailbox-test-section'
import { ManagersSection } from './managers-section'

export const RESIDENCE_TABS = [
	'general',
	'sites',
	'services',
	'photos',
	'location',
	'seats',
	'contract',
	'pec',
	'managers'
] as const
export type ResidenceTab = (typeof RESIDENCE_TABS)[number]

const TAB_LABEL: Record<ResidenceTab, TKey> = {
	general: 'admin-residences:tabs.general',
	sites: 'admin-residences:tabs.sites',
	services: 'admin-residences:tabs.services',
	photos: 'admin-residences:tabs.photos',
	location: 'admin-residences:tabs.location',
	seats: 'admin-residences:tabs.seats',
	contract: 'admin-residences:tabs.contract',
	pec: 'admin-residences:tabs.pec',
	managers: 'admin-residences:tabs.managers'
}

/** The tab open on the page: from the address, the general one by default. */
export function useResidenceTab() {
	const { tab } = useSearch({ from: '/(private)/residences/$residenceId/' })
	return tab ?? 'general'
}

/**
 * The residence page in tabs, one per question the admin brings. The field
 * groups stay mounted in every tab, so switching tab keeps what was typed and
 * one save sends it all; a dot marks a tab holding a field to fix. The
 * actions sit on the tab row, so the page spends no row of its own on them.
 */
function ResidenceTabs({
	residence,
	actions
}: {
	residence: Residence
	actions?: ReactNode
}) {
	const { t } = useTranslation()
	const tab = useResidenceTab()
	const navigate = useNavigate({ from: '/residences/$residenceId/' })

	return (
		<Tabs
			onValueChange={(value: ResidenceTab) =>
				navigate({
					search: (prev) => ({ ...prev, tab: value }),
					replace: true
				})
			}
			value={tab}
		>
			<div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-3'>
				{/* Scrolls sideways on a phone instead of widening the page. */}
				<div className='-mx-4 max-w-full overflow-x-auto overflow-y-hidden border-border border-b px-4 md:mx-0 md:px-0'>
					<TabsList variant='line'>
						{RESIDENCE_TABS.map((value) => (
							<TabsTrigger key={value} value={value}>
								{t(TAB_LABEL[value])}
								{value !== 'managers' &&
									value !== 'photos' &&
									value !== 'location' && (
										<GroupErrorDot
											group={
												value satisfies ResidenceFieldGroup
											}
										/>
									)}
							</TabsTrigger>
						))}
					</TabsList>
				</div>
				{actions}
			</div>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='general'
			>
				<GeneralFields />
			</TabsContent>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='sites'
			>
				<SitesFields />
			</TabsContent>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='services'
			>
				<ServicesFields />
			</TabsContent>
			<TabsContent className='flex flex-col gap-8 pt-4' value='photos'>
				<ResidencePhotos residence={residence} />
			</TabsContent>
			<TabsContent className='flex flex-col gap-8 pt-4' value='location'>
				{residence.sites.map((site) => (
					<LocationSection
						key={site.id}
						residence={residence}
						site={site}
					/>
				))}
			</TabsContent>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='seats'
			>
				<SeatsFields
					dsuRate={{
						single: residence.dsuSingleRateCents,
						double: residence.dsuDoubleRateCents
					}}
				/>
			</TabsContent>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='contract'
			>
				<ContractFields />
			</TabsContent>
			<TabsContent
				className='flex flex-col gap-8 pt-4'
				keepMounted
				value='pec'
			>
				<PecFields />
				<MailboxTestSection
					pecAddress={residence.pecAddress}
					published={residence.call.state !== 'draft'}
					residenceId={residence.id}
				/>
			</TabsContent>
			<TabsContent className='flex flex-col gap-8 pt-4' value='managers'>
				<ManagersSection residenceId={residence.id} />
			</TabsContent>
		</Tabs>
	)
}

export { ResidenceTabs }
